/**
 * AICaptureModal.js
 * Two-phase modal:
 *   Phase 1 — Input: user types keywords → clicks AI Generate
 *   Phase 2 — Preview/Edit: AI result shown with inline-editable fields
 *                           → Confirm fires a fly-to-page animation
 *
 * Fly-to-Page animation:
 *   A ghost card clones out of the modal center and animates along a
 *   curved Framer Motion path to the target A4 sheet's DOM position.
 */
import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { getApiBase } from '../../../../services/apiBase';
import styles from './AICaptureModal.module.css';

// AI responses can occasionally contain arrays or structured values. Textarea
// values and the final note payload must always be plain strings.
function asText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(asText).filter(Boolean).join('\n');
  if (typeof value === 'object') {
    try { return JSON.stringify(value, null, 2); } catch (_) { return String(value); }
  }
  return String(value);
}

export default function AICaptureModal({ onClose, onConfirm, targetSheetRef, subject }) {
  const [phase, setPhase] = useState('input'); // 'input' | 'preview' | 'flying'
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  // Editable preview fields
  const [editTopic, setEditTopic] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [editDetail, setEditDetail] = useState('');
  const [editCategory, setEditCategory] = useState('');

  // Flying card state
  const [flyPos, setFlyPos] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const modalRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (phase === 'input') {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [phase]);

  // Sync edit fields when preview arrives
  useEffect(() => {
    if (preview) {
      setEditTopic(asText(preview.topic));
      setEditSummary(asText(preview.summary));
      setEditDetail(asText(preview.detail));
      setEditCategory(asText(preview.category) || 'Data Science');
    }
  }, [preview]);

  // ── AI Generation ────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!input.trim() || loading) return;
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      const API = axios.create({ baseURL: getApiBase(), timeout: 30000 });
      const { data } = await API.post('/api/cs/generate-card', {
        input: input.trim(),
        subject: subject || 'Data Science',
      });
      setPreview({
        ...data,
        id: `card-${Date.now()}`,
        created_at: Date.now(),
        review_count: 0,
        ease_factor: 2.5,
      });
      setPhase('preview');
    } catch (err) {
      setError(err.response?.data?.error || 'AI generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Confirm & Fly ────────────────────────────────────────────────────────
  const handleConfirm = () => {
    const confirmed = {
      ...preview,
      topic: asText(editTopic).trim() || asText(preview.topic),
      summary: asText(editSummary).trim() || asText(preview.summary),
      detail: asText(editDetail).trim() || asText(preview.detail),
      category: asText(editCategory).trim() || asText(preview.category),
    };

    // Compute source position (center of modal)
    const modalEl = modalRef.current;
    const srcRect = modalEl?.getBoundingClientRect();
    const srcPos = srcRect
      ? { x: srcRect.left + srcRect.width / 2 - 80, y: srcRect.top + srcRect.height / 2 - 40 }
      : { x: window.innerWidth / 2 - 80, y: window.innerHeight / 2 - 40 };

    // Compute destination (target A4 sheet)
    const tgtEl = targetSheetRef?.current;
    const tgtRect = tgtEl?.getBoundingClientRect();
    const tgtPos = tgtRect
      ? { x: tgtRect.left + tgtRect.width / 2 - 80, y: tgtRect.top + tgtRect.height / 2 - 40 }
      : { x: window.innerWidth / 2 - 80, y: window.innerHeight - 200 };

    setFlyPos(srcPos);
    setFlyTarget(tgtPos);
    setPhase('flying');

    // After fly animation completes, add note and close
    setTimeout(() => {
      onConfirm(confirmed);
    }, 680);
  };

  const handleRetry = () => {
    setPhase('input');
    setPreview(null);
    setError('');
  };

  return (
    <AnimatePresence>
      {/* ── Overlay (hidden during fly phase) ───────────────────────────── */}
      {phase !== 'flying' && (
        <motion.div
          className={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.div
            ref={modalRef}
            className={styles.modal}
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 10 }}
            transition={{ type: 'spring', stiffness: 340, damping: 28 }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                <div className={styles.modalTitleIcon}>✨</div>
                <div>
                  <div className={styles.modalTitleText}>Quick Note (AI)</div>
                  <div className={styles.modalSubtitle}>
                    {phase === 'input' ? 'Describe what you learned' : 'Review & edit before saving'}
                  </div>
                </div>
              </div>
              <button className={styles.closeBtn} onClick={onClose}>✕</button>
            </div>

            {/* Body */}
            <div className={styles.modalBody}>
              <AnimatePresence mode="wait">

                {/* ── Phase 1: Input ─────────────────────────────────────── */}
                {phase === 'input' && (
                  <motion.div
                    key="input"
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 12 }}
                    transition={{ duration: 0.18 }}
                    style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                  >
                    <label className={styles.inputLabel}>
                      What did you learn today? Enter keywords or a brief description:
                    </label>
                    <textarea
                      ref={inputRef}
                      className={styles.keywordInput}
                      placeholder="e.g. learned numpy array slicing today, or: pandas groupby aggregation with multiple functions..."
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && !loading && handleGenerate()}
                      rows={3}
                      style={{ resize: 'none' }}
                    />
                    {error && <p className={styles.errorMsg}>{error}</p>}
                    <button
                      className={styles.generateBtn}
                      onClick={handleGenerate}
                      disabled={loading || !input.trim()}
                    >
                      {loading ? (
                        <><span className={styles.spinner} /> AI Extracting...</>
                      ) : (
                        <>✨ AI Generate Card</>
                      )}
                    </button>
                  </motion.div>
                )}

                {/* ── Phase 2: Preview / Edit ────────────────────────────── */}
                {phase === 'preview' && preview && (
                  <motion.div
                    key="preview"
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.18 }}
                    style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                  >
                    {/* Success banner */}
                    <div className={styles.previewBanner}>
                      <div className={styles.previewBannerDot} />
                      Card generated! Edit any field below before saving.
                    </div>

                    {/* Editable card */}
                    <div className={styles.previewCard}>
                      <div className={styles.previewCardInner}>
                        <div className={styles.editFieldGroup}>
                          <label className={styles.editFieldLabel}>Topic</label>
                          <input
                            className={styles.editFieldInput}
                            value={editTopic}
                            onChange={e => setEditTopic(e.target.value)}
                            autoFocus
                          />
                        </div>

                        <div className={styles.editFieldGroup}>
                          <label className={styles.editFieldLabel}>Summary</label>
                          <textarea
                            className={`${styles.editFieldInput} ${styles.editFieldTextarea}`}
                            value={editSummary}
                            onChange={e => setEditSummary(e.target.value)}
                            rows={2}
                          />
                        </div>

                        <div className={styles.editFieldGroup}>
                          <label className={styles.editFieldLabel}>Detail / Code</label>
                          <textarea
                            className={`${styles.editFieldInput} ${styles.editFieldTextarea} ${styles.editFieldCode}`}
                            value={editDetail}
                            onChange={e => setEditDetail(e.target.value)}
                            rows={3}
                          />
                        </div>

                        <div className={styles.editFieldGroup}>
                          <label className={styles.editFieldLabel}>Category</label>
                          <input
                            className={styles.editFieldInput}
                            value={editCategory}
                            onChange={e => setEditCategory(e.target.value)}
                          />
                        </div>

                        {(preview.tags || []).length > 0 && (
                          <div>
                            <label className={styles.editFieldLabel} style={{ marginBottom: 4, display: 'block' }}>Tags</label>
                            <div className={styles.tagsRow}>
                              {(preview.tags || []).map(t => (
                                <span key={t} className={styles.tagChip}>{t}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className={styles.previewActions}>
                      <button className={styles.retryBtn} onClick={handleRetry}>
                        ← Retry
                      </button>
                      <button className={styles.confirmBtn} onClick={handleConfirm}>
                        📌 Add to Canvas
                      </button>
                    </div>
                  </motion.div>
                )}

              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* ── Flying card animation ──────────────────────────────────────────── */}
      {phase === 'flying' && flyPos && flyTarget && preview && (
        <motion.div
          className={styles.flyingCard}
          style={{ left: flyPos.x, top: flyPos.y, position: 'fixed' }}
          initial={{ scale: 1, opacity: 1, x: 0, y: 0 }}
          animate={{
            x: flyTarget.x - flyPos.x,
            y: flyTarget.y - flyPos.y,
            scale: 0.25,
            opacity: 0,
            rotate: [0, -3, 4, -2, 0],
          }}
          transition={{
            duration: 0.65,
            ease: [0.25, 0.46, 0.45, 0.94],
            rotate: { duration: 0.65, ease: 'easeInOut' },
          }}
        >
          <p className={styles.flyingCardTopic}>{editTopic || preview.topic}</p>
          <p className={styles.flyingCardSummary}>{editSummary || preview.summary}</p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
